import { useState, useCallback } from 'react';
import { useProcessing } from '../context/ProcessingContext';
import { useNotifications } from '../context/NotificationContext';
import { useOrganization } from './useOrganization';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { notifyQuotaChanged, openUpgradeModal, parseQuotaExceeded, quotaExceededMessage } from '../lib/quotaErrors';

const ALLOWED_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// À partir de ce nombre de CVs d'un coup, on bascule sur la Batch API Gemini
// (-50 % de coût, traitement asynchrone) via create-cv-batch.
//
// Descendu de 20 à 5 le 2026-08-20. Ce seuil n'est pas qu'une affaire de coût :
// c'est notre SEULE limite de concurrence. En dessous, chaque CV déclenche une
// analyse de fond indépendante, et elles se ralentissent mutuellement — cinq CV
// déposés ensemble ont fait dépasser le délai à trois d'entre eux, là où les
// mêmes passaient seuls en 40 s. Au-delà du seuil, un seul job part chez Google,
// qui l'ordonnance lui-même : plus rien ne se marche dessus.
export const BATCH_MODE_THRESHOLD = 5;
// Tranches envoyées à create-cv-batch : bornées en nombre et en poids
// pour rester sous la limite de payload des edge functions.
const BATCH_CHUNK_MAX_FILES = 10;
const BATCH_CHUNK_MAX_BYTES = 15 * 1024 * 1024;
// Tranches envoyees en parallele, par petits paquets. Elles partaient une par une,
// chacune attendant les ~30 s du serveur : 66 fichiers = 7 tranches = 3 min 30 pendant
// lesquelles tout l'import reposait sur l'onglet. Un rechargement en cours de route
// perdait silencieusement les tranches restantes — 56 fichiers sur 66 le 2026-08-21.
// Trois de front ramenent la fenetre a environ une minute, sans saturer la connexion.
const SEND_CONCURRENCY = 3;

export interface BatchUploadResult {
    file: string;
    success: boolean;
    candidateId?: string;
    error?: string;
    /** true si le CV est parti en traitement par lot (résultat différé) */
    queued?: boolean;
}

export const useUpload = () => {
    const [isUploading, setIsUploading] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { startMonitoring } = useProcessing();
    const { notify } = useNotifications();
    const { org } = useOrganization();
    const { user } = useAuth();

    // 429 quota (vivier plein) : le message nomme le quota atteint et la
    // notification propose la montée en gamme — jamais un « erreur 429 » brut.
    const quotaErrorFromResponse = useCallback(async (response: Response): Promise<string | null> => {
        if (response.status !== 429) return null;
        const body = await response.json().catch(() => null);
        const info = parseQuotaExceeded(response.status, body);
        if (!info) return null;
        const msg = quotaExceededMessage(info);
        notify('error', 'Quota atteint', {
            description: msg,
            action: { label: 'Voir les forfaits', onClick: openUpgradeModal },
        });
        return msg;
    }, [notify]);

    const uploadCandidate = useCallback(async (file: File) => {
        setIsUploading(true);
        setError(null);

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            const msg = `Type de fichier non supporté (${file.type || 'inconnu'}). Utilisez PDF ou Word.`;
            setError(msg);
            setIsUploading(false);
            return { success: false, error: msg };
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            const msg = `Fichier trop lourd (max 10 Mo) : ${file.name}`;
            setError(msg);
            setIsUploading(false);
            return { success: false, error: msg };
        }

        const webhookUrl = import.meta.env.VITE_EDGE_CREATE_CV ?? import.meta.env.VITE_EDGE_CREATE_CV_REC;
        if (!webhookUrl) {
            const msg = 'VITE_EDGE_CREATE_CV / _REC is not defined in .env';
            setError(msg);
            setIsUploading(false);
            return { success: false, error: msg };
        }

        const formData = new FormData();
        formData.append('file', file);
        if (org?.id) formData.append('organization_id', org.id);
        if (user?.id) formData.append('created_by', user.id);

        try {
            const params = new URLSearchParams();
            if (org?.id) params.set('organization_id', org.id);
            if (user?.id) params.set('created_by', user.id);
            const url = params.toString() ? `${webhookUrl}?${params}` : webhookUrl;

            const { data: { session } } = await supabase.auth.getSession();
            const authHeaders: Record<string, string> = {
                'Authorization': `Bearer ${session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            };

            const response = await fetch(url, { method: 'POST', body: formData, headers: authHeaders });

            if (!response.ok) {
                const quotaMsg = await quotaErrorFromResponse(response);
                if (quotaMsg) {
                    setError(quotaMsg);
                    setIsUploading(false);
                    return { success: false, error: quotaMsg };
                }
                throw new Error(`Erreur upload: ${response.statusText}`);
            }

            const result = await response.json();
            notifyQuotaChanged();
            const rawId = result.candidate_id;
            const candidateId = rawId && !String(rawId).includes('{{') ? String(rawId) : null;
            if (candidateId) {
                startMonitoring(candidateId, file.name, file);
                if (user?.id) {
                    supabase.from('candidates').update({ created_by: user.id } as any).eq('id', Number(candidateId)).then(() => {});
                }
                setIsUploading(false);
                return { success: true, candidateId };
            } else {
                startMonitoring(`temp-${Date.now()}`, file.name, file);
                setIsUploading(false);
                return { success: true };
            }
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.message || "Une erreur est survenue lors de l'envoi.");
            setIsUploading(false);
            return { success: false, error: err.message };
        }
    }, [startMonitoring, org, user, quotaErrorFromResponse]);

    const uploadCandidates = useCallback(async (files: File[]): Promise<BatchUploadResult[]> => {
        if (files.length === 0) return [];

        const webhookUrl = import.meta.env.VITE_EDGE_CREATE_CV ?? import.meta.env.VITE_EDGE_CREATE_CV_REC;
        if (!webhookUrl) {
            const msg = 'VITE_EDGE_CREATE_CV / _REC is not defined in .env';
            setError(msg);
            return files.map(f => ({ file: f.name, success: false, error: msg }));
        }

        setIsUploading(true);
        setError(null);
        setBatchProgress({ current: 0, total: files.length });

        const { data: { session: batchSession } } = await supabase.auth.getSession();
        const batchAuthHeaders: Record<string, string> = {
            'Authorization': `Bearer ${batchSession?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        };

        const results: BatchUploadResult[] = [];

        // ── Mode lot (Batch API Gemini) au-delà du seuil ─────────────────────
        const validFiles = files.filter(f => ALLOWED_MIME_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_BYTES);
        // `>=` : le seuil se lit « à partir de 5 », pas « au-delà de 5 ».
        if (validFiles.length >= BATCH_MODE_THRESHOLD) {
            // Les fichiers invalides sont signalés immédiatement
            for (const f of files) {
                if (!ALLOWED_MIME_TYPES.includes(f.type)) {
                    results.push({ file: f.name, success: false, error: `Type non supporté: ${f.type || 'inconnu'}` });
                } else if (f.size > MAX_FILE_SIZE_BYTES) {
                    results.push({ file: f.name, success: false, error: 'Fichier trop lourd (max 10 Mo)' });
                }
            }

            const batchUrl = webhookUrl.replace(/\/[^/]+\/?$/, '/create-cv-batch');

            // Découpage en tranches (nombre + poids) pour la limite de payload
            const chunks: File[][] = [];
            let current: File[] = [];
            let currentBytes = 0;
            for (const f of validFiles) {
                if (current.length >= BATCH_CHUNK_MAX_FILES || currentBytes + f.size > BATCH_CHUNK_MAX_BYTES) {
                    if (current.length) chunks.push(current);
                    current = [];
                    currentBytes = 0;
                }
                current.push(f);
                currentBytes += f.size;
            }
            if (current.length) chunks.push(current);

            const sendChunk = async (chunk: File[]): Promise<BatchUploadResult[]> => {
                const fd = new FormData();
                for (const f of chunk) fd.append('files', f);
                if (org?.id) fd.append('organization_id', org.id);
                if (user?.id) fd.append('created_by', user.id);

                try {
                    const resp = await fetch(batchUrl, { method: 'POST', body: fd, headers: batchAuthHeaders });
                    if (!resp.ok) {
                        const quotaMsg = await quotaErrorFromResponse(resp);
                        if (quotaMsg) {
                            // Vivier plein. Les tranches déjà parties seront refusées de la
                            // même façon et le diront chacune pour elle-même — on ne peut plus
                            // les arrêter, et c'est sans conséquence : le serveur refuse.
                            setError(quotaMsg);
                            return chunk.map(f => ({ file: f.name, success: false, error: quotaMsg }));
                        }
                        throw new Error(`Erreur envoi du lot (${resp.status})`);
                    }
                    const data = await resp.json();
                    const skippedNames = new Set<string>((data.skipped ?? []).map((s: { file: string }) => s.file));
                    return chunk.map(f => skippedNames.has(f.name)
                        ? { file: f.name, success: true, error: 'Déjà analysé (doublon ignoré)' }
                        : { file: f.name, success: true, queued: true });
                } catch (err: any) {
                    return chunk.map(f => ({ file: f.name, success: false, error: err.message }));
                }
            };

            // File d'attente partagée : chaque ouvrier prend la tranche suivante dès qu'il
            // se libère, plutôt que d'attendre que tout un paquet soit terminé.
            let sent = 0;
            let cursor = 0;
            const workers = Array.from({ length: Math.min(SEND_CONCURRENCY, chunks.length) }, async () => {
                while (cursor < chunks.length) {
                    const chunk = chunks[cursor++];
                    const chunkResults = await sendChunk(chunk);
                    results.push(...chunkResults);
                    sent += chunk.length;
                    setBatchProgress({ current: Math.min(sent, validFiles.length), total: validFiles.length });
                }
            });
            await Promise.all(workers);

            setIsUploading(false);
            setBatchProgress(null);
            return results;
        }

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setBatchProgress({ current: i + 1, total: files.length });

            if (!ALLOWED_MIME_TYPES.includes(file.type)) {
                results.push({ file: file.name, success: false, error: `Type non supporté: ${file.type || 'inconnu'}` });
                continue;
            }
            if (file.size > MAX_FILE_SIZE_BYTES) {
                results.push({ file: file.name, success: false, error: 'Fichier trop lourd (max 10 Mo)' });
                continue;
            }

            const formData = new FormData();
            formData.append('file', file);
            if (org?.id) formData.append('organization_id', org.id);
            if (user?.id) formData.append('created_by', user.id);

            try {
                const params = new URLSearchParams();
                if (org?.id) params.set('organization_id', org.id);
                if (user?.id) params.set('created_by', user.id);
                const url = params.toString() ? `${webhookUrl}?${params}` : webhookUrl;
                const response = await fetch(url, { method: 'POST', body: formData, headers: batchAuthHeaders });

                if (!response.ok) {
                    const quotaMsg = await quotaErrorFromResponse(response);
                    if (quotaMsg) {
                        // Vivier plein : inutile d'envoyer les fichiers restants.
                        setError(quotaMsg);
                        for (let j = i; j < files.length; j++) {
                            results.push({ file: files[j].name, success: false, error: quotaMsg });
                        }
                        break;
                    }
                    throw new Error(`Erreur upload: ${response.statusText}`);
                }

                const result = await response.json();
                const rawId = result.candidate_id;
                const candidateId = rawId && !String(rawId).includes('{{') ? String(rawId) : null;
                if (candidateId) {
                    startMonitoring(candidateId, file.name, file);
                    if (user?.id) {
                        supabase.from('candidates').update({ created_by: user.id } as any).eq('id', Number(candidateId)).then(() => {});
                    }
                    results.push({ file: file.name, success: true, candidateId });
                } else {
                    startMonitoring(`temp-${Date.now()}`, file.name, file);
                    results.push({ file: file.name, success: true });
                }
            } catch (err: any) {
                results.push({ file: file.name, success: false, error: err.message });
            }
        }

        setIsUploading(false);
        setBatchProgress(null);
        notifyQuotaChanged();
        return results;
    }, [startMonitoring, org, user, quotaErrorFromResponse]);

    return { uploadCandidate, uploadCandidates, batchProgress, isUploading, error };
};
